import { t } from '../../i18n/i18n';

const BASE_URL = (process.env.REACT_APP_CANDLE_CLASH_API_URL || '').replace(/\/$/, '');
const MOCK_KEY = 'candleClashMockDb';

export const PTS_PIPE = 250;
export const PTS_COIN = 100;

async function call(method, path, payload) {
  const isGet = method === 'GET';
  const query = isGet ? `?${new URLSearchParams(payload)}` : '';
  const res = await fetch(`${BASE_URL}${path}${query}`, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: isGet ? undefined : JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || t('Request failed ({{status}})', { status: res.status }));
  return data;
}

const endpoint = (method, path, mock) => (payload) =>
  BASE_URL
    ? call(method, path, payload)
    : new Promise((resolve) => setTimeout(resolve, 250)).then(() => mock(payload));

const rewardTiers = [
  { min: 1000, percent: '10%' },
  { min: 2500, percent: '15%' },
  { min: 5000, percent: '20%' },
  { min: 10000, percent: '30%' },
];

const SEED_NAMES = ['PixelKing', 'ClashPro', 'BirdNerd', 'CandleLord', 'AlphaAce', 'ChartNinja', 'MoonRunner', 'PipPilot', 'TradeWave', 'SignalByte', 'GreenCandle', 'WickWizard'];
const SEED_BOARD = SEED_NAMES.map((nickname, i) => ({
  nickname,
  score: [4700, 3800, 3100][i] ?? 2950 - i * 180,
}));

const loadDb = () => {
  try {
    return JSON.parse(localStorage.getItem(MOCK_KEY)) || { players: {} };
  } catch {
    return { players: {} };
  }
};
const saveDb = (db) => localStorage.setItem(MOCK_KEY, JSON.stringify(db));
const byId = (db, playerId) => Object.values(db.players).find((p) => p.playerId === playerId);

const rankedBoard = (db, playerId) =>
  [...SEED_BOARD, ...Object.values(db.players).filter((p) => p.best > 0 || p.playerId === playerId).map((p) => ({ nickname: p.nickname, score: p.best, playerId: p.playerId }))]
    .sort((a, b) => b.score - a.score)
    .map((e, i) => ({ rank: i + 1, nickname: e.nickname, score: e.score, isYou: Boolean(playerId) && e.playerId === playerId }));

const rewardCode = (seed) => {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i += 1) h = Math.imul(h ^ seed.charCodeAt(i), 16777619);
  return `CLASH-${(h >>> 0).toString(36).toUpperCase().padStart(6, '0').slice(0, 6)}`;
};

/**
 * POST /candle-clash/players/
 * Request:  { nickname: string, email: string }
 * Response: { playerId: string, best: number }
 * Idempotent on email: a returning player gets the same playerId and their best.
 */
export const registerPlayer = endpoint('POST', '/candle-clash/players/', ({ nickname, email }) => {
  const db = loadDb();
  const player = db.players[email] || { playerId: `p_${Math.random().toString(36).slice(2, 10)}`, email, best: 0, claimedMin: 0 };
  db.players[email] = { ...player, nickname };
  saveDb(db);
  return { playerId: player.playerId, best: player.best };
});

/**
 * POST /candle-clash/scores/
 * Request:  { playerId, score, pipes, coins, durationMs, playedAt }
 *           score must equal pipes * 250 + coins * 100; the server rejects (422) any run that doesn't add up.
 * Response: { best: number, rank: number, isNewBest: boolean,
 *             reward: { id: string, percent: string } | null,    // null if no tier reached or already claimed
 *             tiers: [{ min: number, percent: string }] }        // the full reward ladder, ascending
 */
export const submitScore = endpoint('POST', '/candle-clash/scores/', ({ playerId, score, pipes, coins }) => {
  if (score !== pipes * PTS_PIPE + coins * PTS_COIN) throw new Error('Score does not match the run.');
  const db = loadDb();
  const player = byId(db, playerId);
  if (!player) throw new Error('Unknown player.');
  const isNewBest = player.best > 0 && score > player.best;
  player.best = Math.max(player.best, score);
  saveDb(db);
  const tier = rewardTiers.reduce((acc, t) => (score >= t.min ? t : acc), null);
  const reward = tier && tier.min > player.claimedMin ? { id: `${tier.min}`, percent: tier.percent } : null;
  return { best: player.best, rank: rankedBoard(db, playerId).find((e) => e.isYou).rank, isNewBest, reward, tiers: rewardTiers };
});

/**
 * GET /candle-clash/leaderboard/?playerId=…
 * Response: { entries: [{ rank, nickname, score, isYou }] }   // top 100, plus the caller's row if outside it
 * Emails are never returned.
 */
export const getLeaderboard = endpoint('GET', '/candle-clash/leaderboard/', ({ playerId }) => {
  const rows = rankedBoard(loadDb(), playerId);
  const me = rows.find((e) => e.isYou);
  const top = rows.slice(0, 100);
  return { entries: me && me.rank > 100 ? [...top.slice(0, 99), me] : top };
});

/**
 * POST /candle-clash/rewards/claim/
 * Request:  { playerId, rewardId }
 * Response: { sent: true }   // the server issues the code and emails it to the registered address
 */
export const claimReward = endpoint('POST', '/candle-clash/rewards/claim/', ({ playerId, rewardId }) => {
  const db = loadDb();
  const player = byId(db, playerId);
  if (!player) throw new Error('Unknown player.');
  player.claimedMin = Math.max(player.claimedMin, Number(rewardId));
  saveDb(db);
  console.info(`[candle-clash mock] reward ${rewardCode(`${player.email}|${rewardId}`)} emailed to ${player.email}`);
  return { sent: true };
});
